import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {
	makeGetExamsAuthorizedPassword,
	makeGetExamsAuthorizedPrePassword,
} from 'main/factories/usecases';
import {ExamBookingEnum, OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useCallChatRoom} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {IFormData, StateParams, ValidateParams} from './types';

const {description, theSelectedExam} = translations['pt-br'].others.examAuthorization;

export const useExamAuthorization = () => {
	const [operationState, setOperationState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);
	const [apiModalError, setApiModalError] = useState(false);
	const {specialty, step} = useLocation()?.state as StateParams;
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const methods = useForm<IFormData>({
		defaultValues: {
			authCode: '',
		},
	});

	const {
		handleSubmit,
		formState: {isSubmitting},
		watch,
		reset,
	} = methods;

	const authCodeWatcher = watch('authCode');

	const alertInfoText = `${theSelectedExam} (${formatText(
		specialty?.formattedText,
	)}) ${description}`;

	const handleValidateAuthorizationPassword = async ({authCode}: IFormData) => {
		await validateAuthorizedPrePassword({authCode, specialtyCode: specialty?.code});
	};

	async function validateAuthorizedPassword({authCode, specialtyCode}: ValidateParams) {
		try {
			await makeGetExamsAuthorizedPassword().post({
				cdTipoExame: specialtyCode,
				senhaAutorizada: authCode,
			});
			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			if (error instanceof APIError) {
				reset();
				if (
					error.statusCode === HttpStatusCode.unprocessable ||
					error.statusCode === HttpStatusCode.notFound
				) {
					setApiModalError(true);
				}
			}
		}
	}

	async function validateAuthorizedPrePassword({authCode, specialtyCode}: ValidateParams) {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		try {
			await makeGetExamsAuthorizedPrePassword().post({
				cdTipoExame: specialtyCode,
				preSenhaAutorizada: authCode,
			});
			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (error instanceof APIError) {
				await validateAuthorizedPassword({authCode, specialtyCode});
			}
		}
	}

	const handleBack = () => {
		setActiveBookingFlowStep(step);
		navigate(`${ExamBookingEnum.EXAM_BOOKING_FLOW}`);
	};

	return {
		apiModalError,
		operationState,
		setApiModalError,
		handleBack,
		methods,
		handleSubmit,
		handleValidateAuthorizationPassword,
		alertInfoText,
		callChatRoom,
		authCodeWatcher,
		isSubmitting,
		callChatRoomIsLoading,
	};
};
